import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable, retry, throwError, timer } from 'rxjs';

/** Codigos que responde la plataforma cuando la peticion no llego a ejecutarse
 * y volver a intentarla la resuelve rapido: 429 es el throttle de Lambda cuando
 * dos peticiones caen sobre la misma funcion a la vez, y 502/503 son fallos de
 * borde de API Gateway.
 *
 * El 504 queda deliberadamente fuera: lo devuelve API Gateway cuando la Lambda
 * agoto sus 30 s (pasa a diario en `getClientChannels` y sus hermanas, con
 * `Status: timeout` en CloudWatch). Reintentarlo encadenaria esperas de medio
 * minuto en vez de resolver nada. */
const RETRYABLE_STATUS = [429, 502, 503];

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 300;

/**
 * Reintenta las lecturas que fallaron por saturacion o por un fallo de borde.
 *
 * El dashboard dispara seis peticiones a la vez al cargar y al cambiar de
 * cliente; si dos caen sobre la misma funcion Lambda simultaneamente, la
 * segunda recibe 429 y el `zip` de la pagina descarta las seis, que es lo que
 * el usuario ve como "Algo salio mal". Al reintentar solo las lecturas (GET,
 * idempotentes) el error deja de llegar a la pantalla.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      retry({
        count: MAX_RETRIES,
        delay: (error, retryCount) => {
          if (!this.isRetryable(request, error)) {
            return throwError(() => error);
          }

          /* Backoff exponencial con jitter: dos pestanas que se throttlean
          mutuamente no deben reintentar en el mismo instante. */
          const backoff = BASE_DELAY_MS * Math.pow(2, retryCount - 1);

          return timer(backoff + Math.random() * BASE_DELAY_MS);
        },
      })
    );
  }

  private isRetryable(request: HttpRequest<unknown>, error: unknown): boolean {
    return (
      request.method === 'GET' &&
      error instanceof HttpErrorResponse &&
      RETRYABLE_STATUS.includes(error.status)
    );
  }
}
